import React, { useEffect, useState } from "react";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "components/snackbar";
import { Button } from "components/button";
import { useMutation, useQueryClient } from "react-query";
import { IoCloseCircleOutline } from "react-icons/io5";

interface IProps {
  selectedPlans: any[];
  setSelectedPlans: React.Dispatch<React.SetStateAction<any[]>>;
  buttonSelection: string;
  setButtonSelection: React.Dispatch<React.SetStateAction<string>>;
  setShowSelectedPlans: React.Dispatch<React.SetStateAction<boolean>>;
  setModal: () => void;
}

const ArchiveOrDeleteModal: React.FC<IProps> = ({
  buttonSelection,
  selectedPlans,
  setSelectedPlans,
  setButtonSelection,
  setShowSelectedPlans,
  setModal
}) => {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.ArchiveOrDeleteModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  
  const [localPlans, setLocalPlans] = useState(selectedPlans);

  const isDelete = buttonSelection === "delete";
  const action = isDelete ? "Delete" : "Archive";
  const archiveCount = localPlans.filter(plan => plan.archived).length;
  const unarchivedCount = localPlans.length - archiveCount;

  useEffect(() => {
    if (localPlans.length === 0) {
      setModal();
    }
  }
  , [localPlans]);


  const removePlanFromCustomer = useMutation(
    async (servicePlanIds: string[]) => {
      const res = await Axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/delete-plans`,
        { data: { servicePlanIds } }
      );
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        showSnackbar(tr("Plans deleted successfully"));
      }
    }
  );

  const archivePlans = useMutation(
    async (servicePlanIds: string[]) => {
      const res = await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/archive-plans`,
        { servicePlanIds }
      );
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        showSnackbar(tr("Plan archived successfully"));
      }
    }
  );

  const mutation = isDelete ? removePlanFromCustomer : archivePlans;

  return (
    <div className="px-4">
      <p>{tr("Are you sure you want to")} {tr(action.toLowerCase())} {tr("the selected plans?")}</p>
      <p className={`mb-2 ${isDelete ? "text-red-500" : "text-gray-600"}`}>
        {isDelete ? tr("This action cannot be undone.") : tr("Archived plans can be restored later.")}
      </p>
      <div className="h-[600px] overflow-y-auto">
      {localPlans.map((plan, i) => {
        return (
        <div key={plan.sptc_id} className={`
          p-2 border-b flex items-start justify-between mb-1 rounded-md
          ${plan.archived ? "bg-red-100" : "bg-white"}
        `}>
          <div className="flex items-center justify-start space-x-2">
            <div>{`${i+1}.`}</div>
            <div className="text-gray-700 text-sm">
              <div className="text-primary">
                {plan.plan_name}
              </div>
              <div>
                {plan.customer_name}
              </div>
              <div>
                {plan.property.address} {plan.property.city} {plan.property.state} {plan.property.zipcode}
              </div>
            </div>
          </div>
          {/* Button to remove selected plan */}
          <button 
            onClick={() => {
              const updatedPlans = localPlans.filter(p => p.sptc_id !== plan.sptc_id);
              setLocalPlans(updatedPlans);
              setSelectedPlans(updatedPlans);
              if (updatedPlans.length === 0) {
                setShowSelectedPlans(false);
              }
            }}
            className="text-red-500 hover:bg-red-500 hover:text-white rounded-full"
          >
            <IoCloseCircleOutline size={20} />
          </button>
        </div>
      )})}
      </div>
      <div className="flex mt-4 justify-end space-x-2">
        <Button onClick={() => {
          setButtonSelection("");
          setLocalPlans([]);
          setSelectedPlans([]);
          setShowSelectedPlans(false);
          setModal();
        }}>
          {tr("Cancel")}
        </Button>
        <Button
          primary
          onClick={async () => {
            const planIds = localPlans.map(plan => plan.sptc_id);
            await mutation.mutateAsync(planIds);
            setSelectedPlans([]);
            setButtonSelection("");
            setModal();
          }}
        >
          {action === "Archive" 
            ? (archiveCount > 0 && unarchivedCount === 0 ?
              `${tr("Unarchive")} (${archiveCount})`
              : archiveCount === 0 && unarchivedCount > 0 ?
              `${tr("Archive")} (${unarchivedCount})`
              : `${tr("Archive")} (${archiveCount}) / ${tr("Unarchive")} (${unarchivedCount})`
            )
            : `${tr("Delete")} (${selectedPlans.length})`
          }
        </Button>
      </div>
    </div>
  )
}

export default ArchiveOrDeleteModal